import { gradientList } from "../modules/components/Heatmap/Gradients";
import { supabase } from "../supabase";
import moment from 'moment';
import {
    validateUsername as sharedValidateUsername,
    getUsernameValidationMessage as sharedGetUsernameValidationMessage,
} from "./usernameValidation";

const viteBaseUrl = import.meta.env.VITE_SUPABASE_REDIRECT_URL
const API_BASE_URL = import.meta.env.VITE_BASE_URL || '';

/**
 * Constructs full URL from signed_url for media playback
 * Backend returns relative path: /api/media/[token]
 * This function prepends the API base URL if needed
 * @param {string} signedUrl - The signed URL from backend (can be relative or absolute)
 * @returns {string} - Full URL for media playback
 */
export const getMediaUrl = (signedUrl) => {
    if (!signedUrl) return '';
    
    // If already a full URL (starts with http:// or https://), return as is
    if (signedUrl.startsWith('http://') || signedUrl.startsWith('https://')) {
        return signedUrl;
    }
    
    // If relative path (starts with /), prepend API base URL
    if (signedUrl.startsWith('/')) {
        return `${API_BASE_URL}${signedUrl}`;
    }
    
    // Otherwise return as is (might be blob URL or other format)
    return signedUrl;
};

export const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

export const validateUsername = sharedValidateUsername;
export const getUsernameValidationMessage = sharedGetUsernameValidationMessage;

export const getBioValidationMessage = (bio) => {
    // if (!bio || bio.length === 0) {
    //     return "Bio is required.";
    // }
    if (bio.length > 1000) {
        return "Bio must be no more than 1000 characters long.";
    }
    return null;
}

export const capitalizeFirstLetter = (str) => {
    return str?.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

export const updateEmail = async (newEmail) => {
    const redirectTo = `${viteBaseUrl}/confirm-email`
    const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { redirectTo }
    );

    if (error) {
        console.error("Error sending confirmation link:", error.message);
    } else {
        console.log("Confirmation link sent!");
    }
};


export const formatTime = (date) => {
    const duration = moment.duration(moment().diff(moment.utc(date).local()));

    const seconds = duration.asSeconds();
    const minutes = duration.asMinutes();
    const hours = duration.asHours();
    const days = duration.asDays();

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${Math.floor(minutes)}m`;
    if (hours < 24) return `${Math.floor(hours)}h`;
    if (days < 7) return `${Math.floor(days)}d`;
    return moment.utc(date).local().format('MMM D'); // fallback to something like "Apr 12"
}

export const formatTimeOfDay = (date) => {
    return moment.utc(date).local().format('h:mm A'); // Returns format like "3:17 PM"
}

export const checkCommaCount = (str) => {
    if (!str) return true;
    const commaCount = (str.match(/,/g) || []).length;
    return commaCount <= 2;
};

export const formatTimeAgo = (date) => {
    if (!date) return 'some time ago';

    try {
        const now = new Date();
        const past = new Date(date);

        if (isNaN(past.getTime())) {
            return 'some time ago'; // Invalid date fallback
        }

        const diffMs = now - past;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHr / 24);

        if (diffDay === 0) return 'today';
        if (diffDay === 1) return 'yesterday';
        if (diffDay < 30) return `${diffDay} days ago`;

        const diffMonth = Math.floor(diffDay / 30);
        if (diffMonth === 1) return '1 month ago';
        if (diffMonth < 12) return `${diffMonth} months ago`;

        const diffYear = Math.floor(diffMonth / 12);
        if (diffYear === 1) return '1 year ago';
        return `${diffYear} years ago`;

    } catch (err) {
        console.error('Error formatting date:', err);
        return 'some time ago';
    }
};

export const getDateLabel = (date) => {
    if (!date) return '';

    const messageDate = moment.utc(date).local();
    const today = moment();
    const yesterday = moment().subtract(1, 'day');

    if (messageDate.isSame(today, 'day')) {
        return 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
        return 'Yesterday';
    } else if (messageDate.isAfter(moment().subtract(7, 'days'))) {
        return messageDate.format('dddd'); // Monday, Tuesday, etc.
    } else {
        return messageDate.format('MMMM D, YYYY');
    }
};

export const shouldShowDateLabel = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;

    const currentDate = moment.utc(currentMessage.created_at).local();
    const previousDate = moment.utc(previousMessage.created_at).local();

    return !currentDate.isSame(previousDate, 'day');
};

export function calculateReadingTime(text) {
    if (!text) return 0;
    const wordsPerMinute = 200;
    // Average reading speed    
    const textWithoutHtml = text.replace(/<[^>]*>/g, '');
    // // Remove HTML tags if any    
    const wordCount = textWithoutHtml.split(/\s+/).filter(word => word.length > 0).length;
    // // Count words    
    const readingTimeInMinutes = Math.ceil(wordCount / wordsPerMinute);
    // // Calculate reading time    
    return readingTimeInMinutes;
}

export function getFirstTitleFromHtml(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    // Query for the first heading element (h1-h6)
    const heading = div.querySelector('h1, h2, h3, h4, h5, h6');

    return heading ? heading.textContent.trim() : '';
}



export function separateTitleFromContent(html) {
    if (!html) return { title: '', content: '' };
    const div = document.createElement('div');
    div.innerHTML = html;
    const heading = div.querySelector('h1, h2, h3, h4, h5, h6');
    // let title = '';
    if (heading) {
        // title = heading.textContent.trim();
        heading.parentNode.removeChild(heading);
    }

    return div.innerHTML.trim()
}

export function removeTitleFromHtml(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    const heading = div.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
        heading.parentNode.removeChild(heading);
    }
    return div.innerHTML.trim();
}


export function getFirstParagraphText(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    const p = div.querySelector('p');
    return p ? p.textContent.trim() : '';
}


export function computeMinOuterRadius(tags) {
    const n = tags.length;
    if (n === 1) return tags[0].radius * 1 + 6; // just a bit of padding
    let minR = 0;
    for (let tag of tags) {
        const angle = Math.PI / n;
        const d = tag.radius / Math.sin(angle);
        minR = Math.max(minR, d + tag.radius + 6); // 8px padding
    }
    return minR;
}


export function getGradientIdForRadiusWithinTerrain(radius, tags) {
    const radii = tags.map(t => t.radius || 22);
    const minRadius = Math.min(...radii);
    const maxRadius = Math.max(...radii);
    const numGradients = gradientList.length;

    // Avoid division by zero if all radii are the same
    const t = (maxRadius === minRadius)
        ? 0
        : (radius - minRadius) / (maxRadius - minRadius);

    const idx = Math.max(0, Math.min(Math.floor(t * (numGradients - 1)), numGradients - 1));
    return gradientList[idx].id;
}

import { globalMinRadius, globalMaxRadius } from './data'; // or wherever you put the above code

export function getGradientIdForRadiusGlobal(radius) {
    if (!gradientList || gradientList.length === 0) return undefined;
    const numGradients = gradientList.length;
    const t = (globalMaxRadius === globalMinRadius)
        ? 0
        : (radius - globalMinRadius) / (globalMaxRadius - globalMinRadius);
    const idx = Math.max(0, Math.min(Math.floor((1 - t) * (numGradients - 1)), numGradients - 1));
    // Debug log:
    return gradientList[idx].id;
}

export const getRandomGradientIndex = (list = []) => {
    const gradientIds = gradientList.filter(el => !list.includes(el.id)).map(g => g.id);
    const randomIndex = Math.floor(Math.random() * gradientIds.length);
    return randomIndex;
};


// Helper function to check if content contains HTML
export const hasHtmlContent = (content) => {
    if (!content) return false;
    // Check for HTML tags
    const htmlTagRegex = /<[^>]*>/;
    return htmlTagRegex.test(content);
};

// Helper function to compare arrays of tags
export const areTagsEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    // Sort both arrays and compare
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    return sorted1.every((tag, index) => tag === sorted2[index]);
};

export const htmlToPlainTextServer = (html) => {
    if (!html) return '';

    // Remove HTML tags
    let plainText = html.replace(/<[^>]*>/g, '');

    // Decode common HTML entities
    const entities = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#039;': "'",
        '&nbsp;': ' ',
        '&apos;': "'",
        '&hellip;': '...',
        '&mdash;': '—',
        '&ndash;': '–',
        '&copy;': '©',
        '&reg;': '®',
        '&trade;': '™'
    };

    // Replace HTML entities
    for (const [entity, char] of Object.entries(entities)) {
        plainText = plainText.replace(new RegExp(entity, 'g'), char);
    }

    // Handle numeric character references (&#123; or &#x1F;)
    plainText = plainText.replace(/&#(\d+);/g, (match, dec) => {
        return String.fromCharCode(dec);
    });

    plainText = plainText.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
    });

    // Clean up whitespace
    plainText = plainText.replace(/\s+/g, ' ').trim();

    return plainText;
};
